import { fabric } from 'fabric';
import type { Doc, Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { APP_FMT, fmtNeeded, fmtOf, fromAppWithoutSizes, mixedSizes, sizesOf, splitPages } from '../editor/pageFormat';
import { swapSrc } from '../editor/picture';
import { assets, loadWork, saveErrorText, saveWork, uploadedAs, type ServerLink } from '../editor/storage';
import { thTime } from '../time';
import { $ } from '../ui/dom';
import { toast } from '../ui/toast';
import { api, ApiError, type DesignMeta, type OwnerInfo } from './api';
import { emptyProject, FRAMES, type Frame } from '../video/project';

type Mode = 'checking' | 'online' | 'local';

/**
 * Saving, in two layers:
 *  1. every edit is kept on this device (IndexedDB) within a second;
 *  2. then sent to the server under the signed-in account, if D1 and R2 are set up.
 * Nothing is ever skipped silently: the reason the server copy is behind is always shown.
 */
export function createSaver(ed: Editor, history: History, hooks: {
  onLoaded: () => void; onSignedOut: (reason: string) => void; onViewChange: (v: Viewing | null) => void;
  /** 0015: an admin started or stopped editing a member's work (whose, or null) */
  onAdminEdit: (owner: OwnerInfo | null) => void;
}) {
  let mode: Mode = 'checking';
  let reason = 'กำลังตรวจการเชื่อมต่อเซิร์ฟเวอร์…';
  let email = '';
  let link: ServerLink = { id: null, version: 0, dirty: false };
  let localAt: Date | null = null, serverAt: Date | null = null;
  let localError = '', serverError = '';
  let blocked = '';              // set when the open work did not load completely: saving would lose parts of it
  let viewing: Viewing | null = null;  // someone else's design open read-only: nothing is saved anywhere
  let conflictOpen = false;
  let edits = 0;                 // counts edits so a save that raced with an edit keeps the work dirty
  let localTimer: ReturnType<typeof setTimeout> | undefined;
  let serverTimer: ReturnType<typeof setTimeout> | undefined;
  let inflight: Promise<void> | null = null;
  let opened = 0;                // +1 whenever another work is put on screen (not on undo/redo); see `openedCount`
  /** 0015: the admin's own work to go back to after looking at / editing a member's work (null = a new blank one) */
  let returnTo: string | null = null;
  let shownOwner: string | null = null;   // whose work the admin bar last showed ('' = none)

  /* ---------- status ---------- */
  function render() {
    // the admin bar follows link.owner wherever it changed (opening, going back, a reload of such work)
    const key = link.owner ? link.owner.email : '';
    if (key !== shownOwner) { shownOwner = key; hooks.onAdminEdit(link.owner ?? null); }
    const s = $('save');
    let line: string, isErr = false;
    if (viewing) {
      s.textContent = `ดูอย่างเดียว · งานของ ${viewing.owner.name || viewing.owner.email}`; s.classList.remove('err');
      $('syncDetail').textContent = 'งานที่ได้รับแชร์ ไม่บันทึกการเปลี่ยนแปลง ทำสำเนาเป็นของคุณเพื่อแก้ไข';
      $('syncRetry').hidden = true;
      return;
    }
    if (blocked) { line = 'ไม่บันทึก: งานโหลดไม่ครบ'; isErr = true; }
    else if (localError) { line = 'บันทึกในเครื่องไม่สำเร็จ'; isErr = true; }
    else if (conflictOpen) { line = 'รอเลือก: มีการบันทึกจากอีกเครื่อง'; isErr = true; }
    else if (mode === 'online' && !link.dirty && serverAt) line = `บันทึกขึ้นเซิร์ฟเวอร์แล้ว ${thTime(serverAt)}`;
    else if (mode === 'online' && !link.dirty && link.id) line = 'ตรงกับเซิร์ฟเวอร์แล้ว';
    else if (mode === 'online' && link.dirty) line = serverError ? 'บันทึกในเครื่องแล้ว · ยังไม่ขึ้นเซิร์ฟเวอร์' : 'กำลังบันทึก…';
    else if (localAt) line = `บันทึกในเครื่องแล้ว ${thTime(localAt)}${mode === 'local' ? ' · ไม่ได้ขึ้นเซิร์ฟเวอร์' : ''}`;
    else line = mode === 'checking' ? 'กำลังตรวจการเชื่อมต่อ…' : 'ยังไม่ได้แก้ไข';
    if (serverError && mode === 'online' && link.dirty) isErr = true;
    if (link.owner) line = `แก้แทน ${link.owner.name || link.owner.email} · ${line}`;
    s.textContent = line; s.classList.toggle('err', isErr);

    const detail: string[] = [];
    if (link.owner) detail.push(`งานของ ${link.owner.email} แก้ในฐานะผู้ดูแล บันทึกลงงานของเจ้าของ`);
    if (blocked) detail.push(blocked);
    if (localError) detail.push('ในเครื่อง: ' + localError);
    if (mode === 'local') detail.push('เซิร์ฟเวอร์: ' + reason);
    else if (mode === 'online') {
      detail.push(`เข้าสู่ระบบเป็น ${email}`);
      if (serverError) detail.push('ยังไม่ขึ้นเซิร์ฟเวอร์: ' + serverError);
      else if (serverAt) detail.push(`ขึ้นเซิร์ฟเวอร์ล่าสุด ${thTime(serverAt)} (เวอร์ชัน ${link.version})`);
      else if (!link.id) detail.push('งานนี้ยังไม่เคยขึ้นเซิร์ฟเวอร์ จะขึ้นเองเมื่อแก้ไข');
    }
    $('syncDetail').textContent = detail.join(' · ');
    $('syncRetry').hidden = !(mode === 'local' || serverError) || !!blocked;
  }

  /* ---------- layer 1: this device ---------- */
  async function saveLocalNow() {
    if (blocked || viewing) return;
    try {
      const json = history.snapshot();
      // page 1's size (the open page may be another size: หน้าหลายขนาด)
      await saveWork(json, { ...ed.doc, ...history.pageSize(0) }, link);
      localAt = new Date(); localError = '';
    } catch (e) {
      localError = saveErrorText(e);
      toast('บันทึกงานในเครื่องไม่สำเร็จ: ' + localError, true);
    }
    render();
  }

  /* ---------- layer 2: the server ---------- */

  /** Upload pictures still held only on this device, and point their objects at the server copy. */
  async function uploadPendingImages() {
    const imgs = ed.userObjects().filter((o): o is fabric.Image => o.type === 'image' && ((o as fabric.Image).getSrc?.() ?? '').startsWith('blob:'));
    let again = false;
    for (const img of imgs) {
      const src = img.getSrc();
      const blob = assets.get(src);
      if (!blob) throw new ApiError(0, `ไม่พบไฟล์ของรูป "${(img as fabric.Image & { name?: string }).name ?? ''}" ในเครื่อง จึงอัปโหลดไม่ได้`);
      const { url } = await api.upload(blob);
      // the picture got another file while this one was uploading (แต่งภาพ made it smaller): upload that one instead
      if (img.getSrc() !== src) { again = true; continue; }
      // never setSrc on a picture on the page: a failed load would empty it before saying so (BUG-29)
      const swapped = await swapSrc(img, url, 'อัปโหลดรูปแล้วแต่เปิดรูปจากเซิร์ฟเวอร์ไม่ได้', 1, src).catch((e: Error) => { throw new ApiError(0, e.message); });
      if (!swapped) again = true;
      else uploadedAs.set(src, url);
    }
    if (again) return uploadPendingImages();
    if (imgs.length) ed.canvas.requestRenderAll();
    // pictures added on other pages while offline: upload them and fix their address in those pages
    for (const url of history.blobUrlsOffCanvas()) {
      const blob = assets.get(url);
      if (!blob) throw new ApiError(0, 'ไม่พบไฟล์ของรูปในหน้าอื่นของงานนี้ในเครื่อง จึงอัปโหลดไม่ได้');
      const { url: serverUrl } = await api.upload(blob);
      history.replaceOffCanvas(url, serverUrl);
    }
  }

  /** the design's width/height (D1) are page 1's — the open page may be another size (หน้าหลายขนาด) */
  const payload = () => {
    const json = history.snapshot(), one = history.pageSize(0);
    return { name: ed.doc.name, width: Math.round(one.w), height: Math.round(one.h), bg: ed.doc.bg, json };
  };

  async function saveServerNow(): Promise<void> {
    if (mode !== 'online' || blocked || viewing || conflictOpen || !link.dirty) return;
    if (inflight) { await inflight; return saveServerNow(); }
    const startEdits = edits;
    inflight = (async () => {
      try {
        await uploadPendingImages();
        // a page may have opened incomplete (or someone else's design opened) while pictures were uploading
        if (blocked || viewing) return;
        const res = link.id ? await api.save(link.id, payload(), link.version) : await api.create(payload());
        link.id = res.design.id; link.version = res.design.version;
        serverAt = new Date(); serverError = '';
        if (edits === startEdits) link.dirty = false;
        await saveLocalNow();           // remember the server version on this device too
      } catch (e) {
        const err = e instanceof ApiError ? e : new ApiError(0, e instanceof Error ? e.message : String(e));
        if (err.status === 409) showConflict(err);
        else if (err.status === 401) { mode = 'local'; reason = err.message; serverError = ''; hooks.onSignedOut(err.message); }
        else if (err.status === 403 || err.status === 503) { mode = 'local'; reason = err.message; serverError = ''; }
        else { serverError = err.message; clearTimeout(serverTimer); serverTimer = setTimeout(() => void saveServerNow(), 30_000); }
      } finally {
        inflight = null;
        render();
      }
    })();
    await inflight;
    if (link.dirty && !serverError && !conflictOpen && mode === 'online') scheduleServer(); // edits made during the save
  }

  function scheduleServer() { clearTimeout(serverTimer); serverTimer = setTimeout(() => void saveServerNow(), 2500); }

  /** Called on every edit. */
  function changed() {
    if (blocked || viewing) return;
    edits++; link.dirty = true;
    clearTimeout(localTimer);
    localTimer = setTimeout(() => void saveLocalNow(), 800);
    if (mode === 'online') scheduleServer();
    render();
  }

  /* ---------- conflict: another device saved first ---------- */
  function showConflict(err: ApiError) {
    conflictOpen = true;
    $('conflictMsg').textContent = err.message + ' เลือกได้ว่าจะเก็บงานบนจอนี้ไว้เป็นสำเนาใหม่ หรือเปิดฉบับล่าสุดแทน';
    $('conflict').hidden = false;
  }
  function closeConflict() { conflictOpen = false; $('conflict').hidden = true; render(); }

  $('cfCopy').onclick = async () => {
    try {
      const res = await api.create({ ...payload(), name: `${ed.doc.name} (สำเนา)`.slice(0, 120) });
      ed.doc.name = res.design.name;
      link = { id: res.design.id, version: res.design.version, dirty: false };
      serverAt = new Date(); serverError = '';
      closeConflict(); hooks.onLoaded(); await saveLocalNow();
      toast(`เก็บเป็นงานใหม่ชื่อ "${res.design.name}" แล้ว`);
    } catch (e) { toast('สร้างสำเนาไม่สำเร็จ: ' + (e instanceof Error ? e.message : String(e)), true); }
  };
  $('cfLatest').onclick = async () => {
    if (!link.id) return;
    try {
      const res = await api.get(link.id);
      await openDesign(res.design, res.json, link.owner);   // still the member's work when an admin was editing it
      closeConflict();
      toast('เปิดฉบับล่าสุดแล้ว');
    } catch (e) { toast('เปิดฉบับล่าสุดไม่สำเร็จ: ' + (e instanceof Error ? e.message : String(e)), true); }
  };
  $('syncRetry').onclick = async () => { serverError = ''; await check(); if (link.dirty) await saveServerNow(); render(); };

  /* ---------- loading ---------- */

  /**
   * Put a design on the canvas (page 1). Saving stops if any page opens incomplete: history.ts reports it
   * through block() for this page now and for each other page when it is opened.
   */
  function loadInto(json: string, doc: Partial<Doc>): Promise<void> {
    opened++;
    Object.assign(ed.doc, doc);
    ed.setDocSize(ed.doc.w, ed.doc.h);
    ed.setDocBg(ed.doc.bg);
    blocked = '';
    return new Promise((res, rej) => {
      const failed = (e: unknown) => {
        blocked = 'เปิดงานไม่ได้: ' + (e instanceof Error ? e.message : String(e)) + ' ระบบจึงไม่บันทึกทับ';
        toast(blocked, true); render(); rej(e);
      };
      try {
        history.load(json, () => {
          history.reset();
          hooks.onLoaded(); render(); res();
        }, 0, failed, { w: ed.doc.w, h: ed.doc.h });
      } catch (e) { failed(e); }
    });
  }

  /** A page opened with missing objects: stop saving so the stored design is not overwritten without them. */
  function block(reason: string) {
    if (viewing) return;
    blocked = reason;
    clearTimeout(localTimer); clearTimeout(serverTimer);
    toast(reason, true); render();
  }

  /**
   * Work kept on this device that an app from before page sizes changed (no `size`, several pages, not yet on the
   * server): its pages were all shown at page 1's size. Uploading it would flatten a server design whose pages
   * differ, so it waits until the server copy says its pages are all one size; otherwise saving stops with the reason.
   */
  async function checkRestored(json: string) {
    if (!link.id || !link.dirty || viewing) return;
    // left by an app from before page sizes / before เลขรุ่นของงาน (0028: it wrote no "fmt", or a lower one)
    const oldSizes = fromAppWithoutSizes(json), knew = fmtOf(json);
    if (!oldSizes && knew >= APP_FMT) return;
    const stop = (what: string, why: string) => block(`งานที่ค้างในเครื่องนี้ถูกแก้ด้วยแอปรุ่นเก่าที่ไม่รู้จัก${what} ${why} ระบบจึงหยุดบันทึกขึ้นเซิร์ฟเวอร์ เปิดงานนี้จาก "งานของฉัน" อีกครั้งเพื่อใช้ฉบับบนเซิร์ฟเวอร์`);
    try {
      const res = await api.get(link.id);
      const split = splitPages(res.json);
      if (oldSizes && mixedSizes(sizesOf(split, { w: res.design.width, h: res.design.height }))) stop('หน้าหลายขนาด', 'และงานบนเซิร์ฟเวอร์มีหน้าต่างขนาดกัน ถ้าบันทึกทับ ทุกหน้าจะกลายเป็นขนาดหน้าแรก');
      else if (knew < fmtNeeded(res.json)) stop('ข้อมูลรุ่นใหม่ (เช่น ช่องข้อมูลเอกสาร ข้อความสองภาษา)', 'และงานบนเซิร์ฟเวอร์มีข้อมูลเหล่านั้น ถ้าบันทึกทับ ข้อมูลจะหาย');
    } catch (e) {
      stop(oldSizes ? 'หน้าหลายขนาด' : 'ข้อมูลรุ่นใหม่', `และตรวจงานบนเซิร์ฟเวอร์ไม่ได้ (${e instanceof Error ? e.message : String(e)}) จึงไม่รู้ว่าบันทึกทับแล้วข้อมูลจะหายไหม`);
    }
  }

  /** `owner` (0015): an admin editing a member's work; without it the work is my own */
  async function openDesign(meta: DesignMeta, json: string, owner?: OwnerInfo) {
    leaveView();
    if (!owner) returnTo = null;
    await loadInto(json, { w: meta.width, h: meta.height, bg: meta.bg, name: meta.name, sizeName: undefined });
    link = owner ? { id: meta.id, version: meta.version, dirty: false, owner: { ...owner } } : { id: meta.id, version: meta.version, dirty: false };
    serverAt = new Date(); serverError = '';
    await saveLocalNow();
  }

  /* ---------- someone else's design, read-only ---------- */

  function lockAll(on: boolean) {
    ed.canvas.discardActiveObject();
    ed.canvas.selection = !on;
    if (on) ed.userObjects().forEach(o => o.set({ selectable: false, evented: false }));
    ed.canvas.requestRenderAll();
  }

  /**
   * Open a shared design read-only — or, `admin` (0015), a member's work an admin may then edit ("แก้ไขในฐานะผู้ดูแล").
   * The caller has already made sure my own open work is safe (flush).
   */
  async function enterView(meta: DesignMeta, json: string, owner: OwnerInfo, admin = false) {
    clearTimeout(localTimer); clearTimeout(serverTimer);
    if (admin && !viewing && !link.owner) returnTo = link.id;
    viewing = { id: meta.id, name: meta.name, owner, admin };
    await loadInto(json, { w: meta.width, h: meta.height, bg: meta.bg, name: meta.name, sizeName: undefined });
    lockAll(true);
    hooks.onViewChange(viewing); render();
  }

  /** Back to my own work as last saved on this device (or a blank design if there is none). */
  async function exitView() {
    leaveView();
    let saved = null;
    try { saved = await loadWork(); }
    catch (e) { toast('เปิดงานที่บันทึกในเครื่องไม่ได้: ' + saveErrorText(e), true); }
    if (saved) {
      await loadInto(saved.json, saved.doc);
      link = saved.server ? { ...saved.server } : { id: null, version: 0, dirty: ed.userObjects().length > 0 };
      localAt = new Date(saved.savedAt); serverAt = null;
      await checkRestored(saved.json);
    } else await newDesign();
    render();
  }

  /** Opening or starting one of my own designs ends read-only mode. */
  function leaveView() {
    if (!viewing) return;
    viewing = null;
    ed.canvas.selection = true;
    hooks.onViewChange(null);
  }

  /** Leave read-only mode straight into a design of mine (e.g. the copy just made). */
  async function exitViewTo(meta: DesignMeta, json: string) {
    await openDesign(meta, json);
    render();
  }

  /**
   * 0015: an admin edits a member's work (after "แก้ไขในฐานะผู้ดูแล", or one just made for them): it saves into
   * the owner's work, the admin bar says whose it is, and "เสร็จแล้ว" goes back to the admin's own work.
   */
  async function openAsAdmin(meta: DesignMeta, json: string, owner: OwnerInfo) {
    if (!viewing && !link.owner) returnTo = link.id;
    await openDesign(meta, json, owner);
    render();
  }

  /** "เสร็จแล้ว": the member's work must be on the server first (or the reason is returned), then back to my own work */
  async function adminDone(): Promise<string | null> {
    if (!link.owner) return null;
    const why = await flush();
    if (why) return why;
    const back = returnTo;
    returnTo = null;
    if (back) {
      try { const res = await api.get(back); await openDesign(res.design, res.json); render(); return null; }
      catch (e) { toast(`เปิดงานของคุณที่ค้างไว้ไม่ได้ (${e instanceof Error ? e.message : String(e)}) จึงเริ่มงานใหม่ให้ เปิดงานเดิมได้จาก "งานของฉัน"`, true); }
    }
    await newDesign();
    render();
    return null;
  }

  /** Start a blank design (not on the server until the first edit). */
  async function newDesign() {
    leaveView();
    returnTo = null;
    await loadInto('{"version":"5.3.0","objects":[]}', { w: 1080, h: 1080, bg: '#ffffff', name: 'งานไม่มีชื่อ', sizeName: 'โพสต์สี่เหลี่ยม' });
    link = { id: null, version: 0, dirty: false };
    serverAt = null; serverError = '';
    await saveLocalNow();
  }

  /** Start an empty video project in one of the two frames (not on the server until the first clip). */
  async function newVideo(frame: Frame) {
    leaveView();
    returnTo = null;
    const f = FRAMES[frame];
    await loadInto(JSON.stringify({ version: '5.3.0', objects: [], extraPages: [], video: emptyProject() }),
      { w: f.w, h: f.h, bg: '#000000', name: 'วิดีโอไม่มีชื่อ', sizeName: `วิดีโอ ${frame}`, bleedMm: undefined });
    link = { id: null, version: 0, dirty: false };
    serverAt = null; serverError = '';
    await saveLocalNow();
  }

  /** Try to get pending edits onto the server; returns a reason if they are still only on this device. */
  async function flush(): Promise<string | null> {
    clearTimeout(serverTimer);
    if (blocked || viewing) return null;  // nothing safe to save / nothing of mine open
    if (link.dirty && mode === 'online') await saveServerNow();
    if (!link.dirty) return null;
    if (conflictOpen) return 'งานที่เปิดอยู่มีการบันทึกจากอีกเครื่อง ต้องเลือกวิธีจัดการก่อน';
    return mode === 'local' ? `งานที่เปิดอยู่ยังไม่ขึ้นเซิร์ฟเวอร์ (${reason})` : `งานที่เปิดอยู่ยังไม่ขึ้นเซิร์ฟเวอร์ (${serverError || 'กำลังบันทึก'})`;
  }

  async function check() {
    try {
      const me = await api.me();
      email = me.user.email;
      if (me.user.status !== 'active') { mode = 'local'; reason = me.user.status === 'pending' ? 'บัญชียังรอผู้ดูแลอนุมัติ' : 'บัญชีนี้ถูกปิดการใช้งาน'; }
      else if (me.server) { mode = 'online'; reason = ''; }
      else { mode = 'local'; reason = 'ยังไม่ได้เชื่อมฐานข้อมูล (D1) และที่เก็บไฟล์ (R2) กับระบบ'; }
    } catch (e) {
      mode = 'local'; reason = e instanceof Error ? e.message : String(e);
      if (e instanceof ApiError && e.status === 401) hooks.onSignedOut(e.message);
    }
    render();
  }

  window.addEventListener('online', () => {
    if (mode === 'online') { if (link.dirty) void saveServerNow(); }
    else void check().then(() => { if (mode === 'online' && link.dirty) void saveServerNow(); });
  });

  return {
    changed, check, flush, newDesign, newVideo, openDesign, loadInto, render, saveLocal: saveLocalNow, block, checkRestored,
    /** someone else's design: lock the objects of a page that was just opened */
    lockView: () => { if (viewing) lockAll(true); },
    enterView, exitView, exitViewTo, get viewing() { return viewing; },
    openAsAdmin, adminDone,
    /** 0015: whose work an admin is editing (null = my own) */
    get editingFor() { return link.owner ?? null; },
    setLink: (l?: ServerLink) => { link = l ? { ...l } : { id: null, version: 0, dirty: false }; },
    setLocalAt: (d: Date) => { localAt = d; },
    start: async () => { await check(); if (mode === 'online' && link.dirty) await saveServerNow(); },
    get mode() { return mode; }, get reason() { return reason; }, get email() { return email; },
    get link() { return link; }, get blocked() { return blocked; },
    /** changes whenever a different work is opened, so a slow job can tell the work it started on is gone */
    get openedCount() { return opened; }
  };
}

export type Saver = ReturnType<typeof createSaver>;
/** `admin` (0015): an admin looking at a member's work, who may switch to editing it */
export interface Viewing { id: string; name: string; owner: OwnerInfo; admin?: boolean }
